import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  setStoredLanguage,
  getTranslation,
} from '../i18n';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang) {
      const matched = AVAILABLE_LANGUAGES.find(lang => lang.code === urlLang);
      if (matched) {
        setStoredLanguage(matched.code);
        // 讀完就清掉，URL 保持乾淨
        params.delete('lang');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState(null, '', newUrl);
        return matched;
      }
    }
    const stored = getStoredLanguage();
    return AVAILABLE_LANGUAGES.find(lang => lang.code === stored) || AVAILABLE_LANGUAGES[0];
  });

  useEffect(() => {
    document.documentElement.lang = currentLanguage.code;
  }, [currentLanguage]);

  const toggleLanguage = useCallback(() => {
    setCurrentLanguage(prev => {
      const currentIndex = AVAILABLE_LANGUAGES.findIndex(lang => lang.code === prev.code);
      const next = AVAILABLE_LANGUAGES[(currentIndex + 1) % AVAILABLE_LANGUAGES.length];
      setStoredLanguage(next.code);
      return next;
    });
  }, []);

  const setLanguage = useCallback((languageCode) => {
    const language = AVAILABLE_LANGUAGES.find(lang => lang.code === languageCode);
    if (language) {
      setCurrentLanguage(language);
      setStoredLanguage(language.code);
    }
  }, []);

  const t = useCallback((key) => {
    return getTranslation(currentLanguage.code, key);
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, toggleLanguage, setLanguage, t, availableLanguages: AVAILABLE_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export default LanguageContext;
